/**
 * Canonical per-container milestone ladder.
 *
 * A fixed, ordered set of physical-journey milestones from empty pickup
 * through empty return, including up to four transshipment ports. Distinct
 * from `types/shipments.ts`'s `MilestonePhase`/`MilestoneType` — those back a
 * company-configurable, shipment-level workflow checklist; this is a fixed,
 * per-container ladder operators fill in manually for shipments with no live
 * provider tracking. Hand-mirrors `shipments/src/types/container-milestones.ts`.
 */

export type ContainerMilestonePhase = 'origin' | 'transshipment' | 'destination' | 'delivery';

export interface CanonicalMilestoneDefinition {
  key: string;
  display: string;
  phase: ContainerMilestonePhase;
  /** Heading of the phase group this milestone belongs to (e.g. "Transshipment Port 2"). */
  group: string;
  /** 1–4 for transshipment milestones. */
  tsIndex?: number;
}

const origin: CanonicalMilestoneDefinition[] = [
  { key: 'empty_pickup', display: 'Container empty pickup from depot', phase: 'origin', group: 'Origin' },
  { key: 'origin_pickup', display: 'Container laden pickup at shipper', phase: 'origin', group: 'Origin' },
  { key: 'pol_arrival', display: 'Container arrival at POL (Gate in)', phase: 'origin', group: 'Origin' },
  { key: 'pol_loaded', display: 'Container loaded at POL', phase: 'origin', group: 'Origin' },
  { key: 'pol_vsldeparture', display: 'Vessel departure from POL', phase: 'origin', group: 'Origin' },
];

const transshipment = (n: number): CanonicalMilestoneDefinition[] => {
  const group = `Transshipment Port ${n}`;
  return [
    { key: `tsp${n}_vslarrival`, display: `Vessel arrival at T/S port ${n}`, phase: 'transshipment', group, tsIndex: n },
    { key: `tsp${n}_discharge`, display: `Container discharge at T/S port ${n}`, phase: 'transshipment', group, tsIndex: n },
    { key: `tsp${n}_loaded`, display: `Container loaded at T/S port ${n}`, phase: 'transshipment', group, tsIndex: n },
    { key: `tsp${n}_vsldeparture`, display: `Vessel departure from T/S ${n}`, phase: 'transshipment', group, tsIndex: n },
  ];
};

const destination: CanonicalMilestoneDefinition[] = [
  { key: 'pod_vslarrival', display: 'Vessel arrival at POD', phase: 'destination', group: 'Destination' },
  { key: 'pod_discharge', display: 'Container discharge at POD', phase: 'destination', group: 'Destination' },
  { key: 'pod_departure', display: 'Container departure from POD (Gate out)', phase: 'destination', group: 'Destination' },
];

const delivery: CanonicalMilestoneDefinition[] = [
  { key: 'lif_arrival', display: 'Container arrival at LIF', phase: 'delivery', group: 'Delivery' },
  { key: 'lif_departure', display: 'Container departure from LIF', phase: 'delivery', group: 'Delivery' },
  { key: 'dlv_delivery', display: 'Container delivery to consignee', phase: 'delivery', group: 'Delivery' },
  { key: 'empty_return', display: 'Container empty return to depot', phase: 'delivery', group: 'Delivery' },
];

export const CANONICAL_MILESTONES: CanonicalMilestoneDefinition[] = [
  ...origin,
  ...transshipment(1),
  ...transshipment(2),
  ...transshipment(3),
  ...transshipment(4),
  ...destination,
  ...delivery,
];

export interface CanonicalMilestoneGroup {
  heading: string;
  phase: ContainerMilestonePhase;
  tsIndex?: number;
  milestones: CanonicalMilestoneDefinition[];
}

/** Ordered, grouped view of the ladder for rendering. */
export const CANONICAL_MILESTONE_GROUPS: CanonicalMilestoneGroup[] = [
  { heading: 'Origin', phase: 'origin', milestones: origin },
  { heading: 'Transshipment Port 1', phase: 'transshipment', tsIndex: 1, milestones: transshipment(1) },
  { heading: 'Transshipment Port 2', phase: 'transshipment', tsIndex: 2, milestones: transshipment(2) },
  { heading: 'Transshipment Port 3', phase: 'transshipment', tsIndex: 3, milestones: transshipment(3) },
  { heading: 'Transshipment Port 4', phase: 'transshipment', tsIndex: 4, milestones: transshipment(4) },
  { heading: 'Destination', phase: 'destination', milestones: destination },
  { heading: 'Delivery', phase: 'delivery', milestones: delivery },
];

const BY_KEY = new Map(CANONICAL_MILESTONES.map((m) => [m.key, m]));
export const canonicalMilestoneByKey = (key: string): CanonicalMilestoneDefinition | undefined => BY_KEY.get(key);

export type MilestoneTimeSource = 'oceanio' | 'manual';

/**
 * Where a milestone happened. Carrier-sourced locations carry real
 * coordinates straight from the feed and can be plotted; a hand-typed one
 * usually has only a `name`, so it shows in the ladder but gets no map pin.
 */
export interface MilestoneLocation {
  name?: string;
  locode?: string;
  country?: string;
  facilityName?: string;
  latitude?: number;
  longitude?: number;
}

/**
 * One (container, canonical milestone) record.
 *
 * The actual is split three ways: `actualFeed` is the carrier's ACT value
 * (sync-owned, always overwritten), `actualCustom` is the operator override
 * (user-owned, never touched by sync), and `actual` is the resolved value
 * (`actualCustom ?? actualFeed`) with `actualSource` recording which won.
 * `actual` is derived server-side — the UI only ever writes `actualCustom`.
 * Clearing `actualCustom` falls the value back to the carrier's.
 */
export interface ContainerMilestone {
  id: string;
  companyId: string;
  shipmentId: string;
  containerId: string;
  canonicalKey: string;
  planned?: string;
  plannedSource?: MilestoneTimeSource;
  plannedInitial?: string;
  plannedInitialSource?: MilestoneTimeSource;
  estimated?: string;
  estimatedSource?: MilestoneTimeSource;
  predictedCreatedAt?: string;
  /** Derived — `actualCustom ?? actualFeed`. Read-only to the client. */
  actual?: string;
  /** Which input won the `actual` resolution. */
  actualSource?: MilestoneTimeSource;
  /** Carrier-reported ACT. Read-only to the client. */
  actualFeed?: string;
  /** Operator override — the only actual the client may write. */
  actualCustom?: string;
  detected?: string;
  detectedSource?: MilestoneTimeSource;
  /** Where this milestone happened — see `MilestoneLocation`. */
  location?: MilestoneLocation;
  /** Manual entry wins over the feed, same rule as `planned`/`estimated`. */
  locationSource?: MilestoneTimeSource;
  note?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Upsert one milestone for one container. A field set to `null` clears that
 * timestamp; `undefined` leaves it unchanged.
 *
 * Note this exposes `actualCustom`, not `actual` — the latter is derived.
 */
export interface UpsertContainerMilestoneDto {
  planned?: string | null;
  estimated?: string | null;
  actualCustom?: string | null;
  location?: MilestoneLocation | null;
  note?: string | null;
}
